/**
 * WizardContext - FMEA Creation Wizard State Management
 * Manages wizard flow, path selection, and project creation data
 */

import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { logger } from '@/utils/logger';
import type { CreateProjectDto } from '@fmeastudio/shared';
import { useWizardPersistence } from '../hooks/useWizardPersistence';
import { API_BASE_URL } from '../api/config';

// Wizard paths
export type WizardPath = 'template' | 'bom' | 'scratch' | 'copy';

// Wizard steps
export type WizardStep =
  | 'welcome' // Path selection
  | 'template-select' // Template path
  | 'bom-upload' // BOM path
  | 'scratch-details' // Scratch path
  | 'copy-select' // Copy path
  | 'review' // Final review
  | 'creating'; // Loading state

// Wizard state interface
export interface WizardState {
  isActive: boolean;
  currentStep: WizardStep;
  selectedPath: WizardPath | null;
  projectData: Partial<CreateProjectDto>;
  templateId?: string;
  bomFile?: File;
  sourceProjectId?: string;
}

// Wizard context type
export interface WizardContextType {
  state: WizardState;
  startWizard: () => void;
  selectPath: (path: WizardPath) => void;
  goToStep: (step: WizardStep) => void;
  updateProjectData: (data: Partial<CreateProjectDto>) => void;
  setTemplateId: (templateId: string) => void;
  completeWizard: () => Promise<void>;
  cancelWizard: () => void;
}

// Initial state
const initialState: WizardState = {
  isActive: false,
  currentStep: 'welcome',
  selectedPath: null,
  projectData: {},
  templateId: undefined,
  bomFile: undefined,
  sourceProjectId: undefined,
};

// Create context
const WizardContext = createContext<WizardContextType | undefined>(undefined);

// Provider props
interface WizardProviderProps {
  children: ReactNode;
}

// Provider component
export function WizardProvider({ children }: WizardProviderProps) {
  const [state, setState] = useState<WizardState>(initialState);

  // fix/request-storm-loop — `onRestore` used to be a fresh inline closure on
  // every WizardProvider render. useWizardPersistence puts it in a dependency
  // array, so an unstable reference here (combined with every action
  // function below ALSO being recreated each render) fed straight into the
  // infinite-loop bug: any consumer with e.g. `startWizard` in a useEffect
  // dependency array (useFirstVisit.ts) would see a "new" function on every
  // render and re-fire forever. useCallback([]) makes it referentially
  // stable across renders.
  const onRestore = useCallback((restoredState: WizardState) => {
    setState(restoredState);
  }, []);
  useWizardPersistence(state, { onRestore });

  // Start wizard (show welcome screen)
  // fix/request-storm-loop — wrapped in useCallback so this stays the SAME
  // function reference across renders (see onRestore comment above for why
  // that matters: an unstable identity here is exactly what turned
  // useFirstVisit's error-recovery effect into an infinite render loop).
  const startWizard = useCallback(() => {
    setState({
      ...initialState,
      isActive: true,
      currentStep: 'welcome',
    });
  }, []);

  // Select a wizard path
  const selectPath = useCallback((path: WizardPath) => {
    // Determine next step based on path
    let nextStep: WizardStep;
    switch (path) {
      case 'template':
        nextStep = 'template-select';
        break;
      case 'bom':
        nextStep = 'bom-upload';
        break;
      case 'scratch':
        nextStep = 'scratch-details';
        break;
      case 'copy':
        nextStep = 'copy-select';
        break;
      default:
        nextStep = 'welcome';
    }

    setState((prev) => ({
      ...prev,
      selectedPath: path,
      currentStep: nextStep,
    }));
  }, []);

  // Navigate to specific step
  const goToStep = useCallback((step: WizardStep) => {
    setState((prev) => ({
      ...prev,
      currentStep: step,
    }));
  }, []);

  // Update project data
  const updateProjectData = useCallback((data: Partial<CreateProjectDto>) => {
    setState((prev) => ({
      ...prev,
      projectData: {
        ...prev.projectData,
        ...data,
      },
    }));
  }, []);

  // Set template ID
  const setTemplateId = useCallback((templateId: string) => {
    setState((prev) => ({
      ...prev,
      templateId,
    }));
  }, []);

  // Complete wizard (create project)
  const completeWizard = useCallback(async () => {
    setState((prev) => ({ ...prev, currentStep: 'creating' }));

    try {
      // Create project based on selected path
      switch (state.selectedPath) {
        case 'template': {
          // Duplicate template project
          if (!state.templateId) {
            throw new Error('No template selected');
          }
          const duplicateResponse = await fetch(`${API_BASE_URL}/projects/${state.templateId}/duplicate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: state.projectData.name || 'Untitled Project',
            }),
          });
          if (!duplicateResponse.ok) {
            throw new Error('Failed to duplicate template');
          }
          // Response is intentionally not retained — the wizard just resets
          // to initialState below on success (see the pre-existing comment
          // about optionally navigating to the new project).
          await duplicateResponse.json();
          break;
        }

        case 'bom':
        case 'scratch':
        case 'copy':
          // These will be implemented in subsequent SoWs
          throw new Error(`${state.selectedPath} path not yet implemented`);

        default:
          throw new Error('No path selected');
      }

      // Reset wizard state
      setState(initialState);

      // Optionally navigate to the new project
      // window.location.href = `/projects/${newProject.id}`;
    } catch (error) {
      logger.error('Failed to create project', error, { module: 'WizardContext' });
      // Keep wizard open on error
      setState((prev) => ({ ...prev, currentStep: 'review' }));
      throw error;
    }
  }, [state.selectedPath, state.templateId, state.projectData.name]);

  // Cancel wizard
  const cancelWizard = useCallback(() => {
    setState(initialState);
  }, []);

  // fix/request-storm-loop — memoize the context value itself. Without this,
  // every WizardProvider render (even ones triggered by something totally
  // unrelated, e.g. a parent context re-rendering) produced a brand new
  // `value` object, which is exactly as damaging as unstable functions for
  // any consumer that puts context fields in a dependency array.
  const value = useMemo<WizardContextType>(() => ({
    state,
    startWizard,
    selectPath,
    goToStep,
    updateProjectData,
    setTemplateId,
    completeWizard,
    cancelWizard,
  }), [state, startWizard, selectPath, goToStep, updateProjectData, setTemplateId, completeWizard, cancelWizard]);

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

// Hook to use wizard context
export function useWizard() {
  const context = useContext(WizardContext);
  if (context === undefined) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
}
